<script setup lang="ts">
import { Link, useForm } from "@inertiajs/vue3"
import { computed } from "vue"
import { UiButton, UiFormLayout, UiSelect, UiTextInput } from "@/components"
import { useNotifications } from "@/composables/useNotifications"

type BomItemPayload = {
  id?: number | null
  component_id: number | ""
  quantity: string
  _destroy?: boolean
}

type BillOfMaterialPayload = {
  id?: number | null
  company_id: number | ""
  product_id: number | ""
  items: BomItemPayload[]
}

const props = defineProps<{
  mode: "create" | "edit"
  title: string
  subtitle?: string
  billOfMaterial: BillOfMaterialPayload
  companies: Array<{ id: number; name: string }>
  parentProducts: Array<{ label: string; value: number }>
  components: Array<{ label: string; value: number }>
  errors?: Record<string, string[]>
}>()

const form = useForm({
  bill_of_material: {
    company_id: props.billOfMaterial.company_id,
    product_id: props.billOfMaterial.product_id,
    items_attributes: props.billOfMaterial.items.map((item) => ({ ...item, _destroy: false })),
  },
})

const notify = useNotifications()
const visibleItems = computed(() => form.bill_of_material.items_attributes.filter((item) => !item._destroy))
const fieldError = (field: string) => props.errors?.[field]?.[0]

function itemError(index: number, field: string) {
  return props.errors?.[`bom_items.${index}.${field}`]?.[0] || props.errors?.[`items_attributes.${index}.${field}`]?.[0]
}

function addItem() {
  form.bill_of_material.items_attributes.push({
    id: null,
    component_id: "",
    quantity: "1",
    _destroy: false,
  })
}

function removeItem(index: number) {
  const item = form.bill_of_material.items_attributes[index]
  if (!item) return

  if (item.id) {
    item._destroy = true
    return
  }

  form.bill_of_material.items_attributes.splice(index, 1)
}

function submit() {
  const opts = { onSuccess: () => notify.success(props.mode === "create" ? "Composicao do produto criada com sucesso" : "Composicao do produto atualizada com sucesso") }

  if (props.mode === "create") {
    form.post("/bill_of_materials", opts)
    return
  }

  form.put(`/bill_of_materials/${props.billOfMaterial.id}`, opts)
}
</script>

<template>
  <UiFormLayout :title="title" :subtitle="subtitle" back-href="/bill_of_materials" back-label="Voltar para composicao do produto">
    <form class="space-y-4" @submit.prevent="submit">
      <div class="grid gap-4 md:grid-cols-2">
        <UiSelect
          id="bom-company"
          :model-value="form.bill_of_material.company_id"
          label="Empresa"
          :disabled="mode === 'edit'"
          :options="companies.map((company) => ({ label: company.name, value: company.id }))"
          :error="fieldError('company') || fieldError('company_id')"
          @update:model-value="form.bill_of_material.company_id = $event as number | ''"
        />

        <UiSelect
          id="bom-parent-product"
          :model-value="form.bill_of_material.product_id"
          label="Produto pai"
          :disabled="mode === 'edit'"
          :options="parentProducts"
          :error="fieldError('product') || fieldError('product_id')"
          @update:model-value="form.bill_of_material.product_id = $event as number | ''"
        />
      </div>

      <section class="rounded-md border border-slate-200 p-4">
        <div class="mb-3 flex items-center justify-between">
          <h2 class="text-sm font-semibold text-slate-800">Componentes</h2>
          <UiButton type="button" size="sm" variant="secondary" @click="addItem">Adicionar componente</UiButton>
        </div>

        <div class="space-y-3">
          <div
            v-for="(item, index) in form.bill_of_material.items_attributes"
            v-show="!item._destroy"
            :key="index"
            class="grid gap-3 rounded-md border border-slate-100 p-3 md:grid-cols-5"
          >
            <div class="md:col-span-3">
              <UiSelect
                :id="`bom-item-component-${index}`"
                :model-value="item.component_id"
                label="Componente"
                :options="components"
                :error="itemError(index, 'component_id')"
                @update:model-value="item.component_id = $event as number | ''"
              />
            </div>

            <UiTextInput
              :id="`bom-item-quantity-${index}`"
              v-model="item.quantity"
              type="number"
              label="Quantidade"
              :error="itemError(index, 'quantity')"
            />

            <div class="flex items-end">
              <UiButton type="button" size="sm" variant="danger" @click="removeItem(index)">Remover</UiButton>
            </div>
          </div>

          <p v-if="visibleItems.length === 0" class="text-sm text-slate-500">Adicione ao menos um componente.</p>
          <p v-if="fieldError('base')" class="text-sm text-rose-600">{{ fieldError('base') }}</p>
        </div>
      </section>

      <div class="flex items-center gap-3 pt-2">
        <UiButton type="submit" :disabled="form.processing">
          {{ mode === "create" ? "Criar composicao" : "Salvar alteracoes" }}
        </UiButton>

        <Link href="/bill_of_materials" class="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100">
          Cancelar
        </Link>
      </div>
    </form>
  </UiFormLayout>
</template>

